import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { MenuIcon, XIcon } from 'lucide-react';
import { Language } from '../App';
type LayoutProps = {
  children: ReactNode;
  language: Language;
  onLanguageChange: (language: Language) => void;
};
const navigation = [{
  to: '/',
  en: 'Home',
  ar: 'الرئيسية'
}, {
  to: '/about',
  en: 'About Us',
  ar: 'من نحن'
}, {
  to: '/services',
  en: 'Financial Solutions',
  ar: 'الحلول المالية'
}, {
  to: '/funding-partners',
  en: 'Investment & Funding',
  ar: 'الاستثمار والتمويل'
}, {
  to: '/faq',
  en: 'FAQ',
  ar: 'الأسئلة الشائعة'
}, {
  to: '/contact',
  en: 'Contact Us',
  ar: 'اتصل بنا'
}];
export function Layout({
  children,
  language,
  onLanguageChange
}: LayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isArabic = language === 'ar';
  return <div className="min-h-screen w-full bg-[#f5f6f7] text-[#08233f]" data-id="element-16">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur" data-id="element-17">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 lg:px-8" data-id="element-18">
          <Link to="/" className="flex items-center gap-3" aria-label="United Third Financial Company home" data-id="element-19">
            <img src="https://cdn.magicpatterns.com/uploads/7HDgQTimqMqqypw55oUi3A/Logo.jpg" alt="United Third Financial Company logo" className="h-16 w-16 object-contain" data-id="element-20" />
            <span className="leading-tight" data-id="element-21">
              <strong className="block text-sm font-bold tracking-[0.12em] text-[#061d34]" data-id="element-22">
                UNITED THIRD
              </strong>
              <span className="mt-1 block text-[10px] font-bold tracking-[0.18em] text-[#9a7227]" data-id="element-23">
                FINANCIAL COMPANY
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-6 lg:flex" aria-label="Main navigation" data-id="element-24">
            {navigation.map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({
            isActive
          }) => `text-xs font-semibold tracking-wide transition-colors hover:text-[#b89142] ${isActive ? 'text-[#b89142]' : 'text-[#29455f]'}`} data-id="element-25">
                {isArabic ? item.ar : item.en}
              </NavLink>)}
          </nav>

          <div className="flex items-center gap-3" data-id="element-26">
            <button onClick={() => onLanguageChange(isArabic ? 'en' : 'ar')} className="border border-[#d7c08b] px-3 py-2 text-xs font-bold tracking-wide text-[#08233f] transition-colors hover:bg-[#f7f1e4]" aria-label="Switch language" data-id="element-27">
              {isArabic ? 'EN' : 'العربية'}
            </button>
            <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-label="Toggle menu" data-id="element-28">
              {menuOpen ? <XIcon size={23} data-id="element-29" /> : <MenuIcon size={23} data-id="element-30" />}
            </button>
          </div>
        </div>
        {menuOpen && <nav className="border-t border-slate-100 bg-white px-5 py-4 lg:hidden" aria-label="Mobile navigation" data-id="element-31">
            {navigation.map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} onClick={() => setMenuOpen(false)} className="block border-b border-slate-100 py-3 text-sm font-semibold text-[#08233f]" data-id="element-32">
                {isArabic ? item.ar : item.en}
              </NavLink>)}
          </nav>}
      </header>
      <main data-id="element-33">{children}</main>
      <footer className="bg-[#061d34] text-slate-300" data-id="element-34">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 py-12 sm:grid-cols-2 lg:grid-cols-3 lg:px-8" data-id="element-35">
          <div data-id="element-36">
            <p className="font-serif text-xl text-white" data-id="element-37">United Third</p>
            <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#d6b45f]" data-id="element-38">
              Financial Company
            </p>
            <p className="mt-5 max-w-sm text-sm leading-6" data-id="element-39">
              {isArabic ? 'شريك موثوق للقروض التجارية والحلول المالية الاستراتيجية والنمو المستدام.' : 'A trusted partner for commercial loans, strategic financing, and sustainable growth.'}
            </p>
          </div>
          <div data-id="element-40">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-[#d6b45f]" data-id="element-41">
              {isArabic ? 'مكتب جدة' : 'Jeddah Office'}
            </p>
            <p className="text-sm leading-7" data-id="element-42">
              {isArabic ? <>
                  طريق المدينة، العيسائي، جدة 23346
                  <br data-id="element-43" />
                  المملكة العربية السعودية
                </> : <>
                  Al-Madinah Road, Alesayi, Jeddah 23346
                  <br data-id="element-44" />
                  Kingdom of Saudi Arabia
                </>}
            </p>
          </div>
          <div className="lg:text-right rtl:lg:text-left" data-id="element-45">
            <p className="text-sm" data-id="element-46">+966 57 278 7010</p>
            <p className="mt-2 text-sm" data-id="element-47">info@unitedthirdfinancialcompany.com</p>
            <p className="mt-2 text-xs text-slate-400" data-id="element-48">
              {isArabic ? 'رقم السجل التجاري: 4030535994' : 'CR No: 4030535994'}
            </p>
            <p className="mt-7 text-xs text-slate-500" data-id="element-49">
              © 2026 United Third Financial Company.{' '}
              {isArabic ? 'جميع الحقوق محفوظة.' : 'All rights reserved.'}
            </p>
          </div>
        </div>
      </footer>
    </div>;
}